import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#0D9488';

const DOCTOR_TABS = [
  { path: '/doctor/dashboard',    icon: 'fa-house',             label: 'Home',     perm: null              },
  { path: '/doctor/appointments', icon: 'fa-calendar-alt',      label: 'Appts',    perm: 'appointments'    },
  { path: '/doctor/slots',        icon: 'fa-clock',             label: 'Slots',    perm: 'slotManagement'  },
  { path: '/doctor/chats',        icon: 'fa-comment-dots',      label: 'Chat',     perm: 'chat'            },
  { path: '/doctor/earnings',     icon: 'fa-indian-rupee-sign', label: 'Earnings', perm: 'earnings'        },
];

const STAFF_TABS = [
  { path: '/doctor/appointments', icon: 'fa-calendar-alt',   label: 'Appts', perm: 'appointments'    },
  { path: '/doctor/slots',        icon: 'fa-clock',          label: 'Slots', perm: 'slotManagement'  },
  { path: '/doctor/chats',        icon: 'fa-comment-dots',   label: 'Chat',  perm: 'chat'            },
];

export default function DoctorBottomNav({ notifCount = 0 }) {
  const { pathname } = useLocation();
  const navigate       = useNavigate();
  const { user, role } = useAuth();
  const permissions    = user?.permissions || {};
  const isStaff        = role === 'staff';

  const tabs = isStaff
    ? STAFF_TABS.filter(t => permissions[t.perm] ?? false)
    : DOCTOR_TABS;

  const isActive = (path) => pathname.startsWith(path);
  const activeIdx = tabs.findIndex(t => isActive(t.path));
  const tabW = 100 / tabs.length;

  const [tapped, setTapped] = useState(null);
  const tapTimer = useRef(null);
  const handleTap = (tab) => {
    setTapped(tab.path);
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => setTapped(null), 360);
    navigate(tab.path);
  };

  return (
    <>
      <style>{`
        @keyframes dnBounce {
          0%   { transform: translateY(0) scale(1); }
          30%  { transform: translateY(-7px) scale(1.22); }
          55%  { transform: translateY(1px) scale(0.93); }
          75%  { transform: translateY(-3px) scale(1.07); }
          100% { transform: translateY(0) scale(1); }
        }
        @keyframes dnPill {
          from { opacity:0; transform:scaleX(0.5); }
          to   { opacity:1; transform:scaleX(1); }
        }
        @keyframes dnBadge {
          0%   { transform:scale(0) rotate(-20deg); }
          65%  { transform:scale(1.35) rotate(4deg); }
          100% { transform:scale(1) rotate(0); }
        }
        .dn-bounce { animation: dnBounce 0.36s cubic-bezier(.36,.07,.19,.97) forwards; }
        .dn-pill   { animation: dnPill   0.2s  ease forwards; }
        .dn-badge  { animation: dnBadge  0.3s  cubic-bezier(.34,1.56,.64,1) forwards; }
      `}</style>

      <nav className="fixed bottom-0 left-0 right-0 z-30 md:hidden"
        style={{
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderTop: '1.5px solid rgba(13,148,136,0.12)',
          paddingBottom: 'env(safe-area-inset-bottom,0px)',
          boxShadow: '0 -4px 24px rgba(13,148,136,0.08)',
        }}>

        {/* Sliding top indicator */}
        {activeIdx >= 0 && (
          <div style={{
            position: 'absolute', top: 0,
            left: `calc(${tabW * activeIdx}% + ${tabW * 0.2}%)`,
            width: `${tabW * 0.6}%`,
            height: '3px',
            borderRadius: '0 0 6px 6px',
            background: PRIMARY,
            transition: 'left 0.32s cubic-bezier(0.34,1.56,0.64,1)',
            boxShadow: `0 2px 8px ${PRIMARY}55`,
          }} />
        )}

        <div className="flex h-16">
          {tabs.map((tab) => {
            const active   = isActive(tab.path);
            const isTapped = tapped === tab.path;
            const hasNotif = (tab.path === '/doctor/chats' || tab.path === '/doctor/dashboard') && notifCount > 0;

            return (
              <button key={tab.path} onClick={() => handleTap(tab)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 relative select-none"
                style={{ outline:'none', WebkitTapHighlightColor:'transparent' }}>

                {active && (
                  <span className="dn-pill absolute inset-x-3 top-1.5 bottom-1.5 rounded-2xl"
                    style={{ background: '#0D948815' }} />
                )}

                <div className="relative z-10">
                  <i className={`fa-solid ${tab.icon} text-xl ${isTapped ? 'dn-bounce' : ''}`}
                    style={{
                      color: active ? PRIMARY : '#94A3B8',
                      transform: active && !isTapped ? 'scale(1.14)' : 'scale(1)',
                      transition: 'color 0.2s, transform 0.2s',
                      display: 'block',
                    }} />
                  {hasNotif && (
                    <span className="dn-badge absolute -top-1.5 -right-2 min-w-[16px] h-4 px-0.5 text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-sm"
                      style={{ background: '#EF4444', lineHeight:1 }}>
                      {notifCount > 9 ? '9+' : notifCount}
                    </span>
                  )}
                </div>

                <span className="text-[10px] font-semibold z-10 leading-none"
                  style={{ color: active ? PRIMARY : '#94A3B8', transition:'color 0.2s' }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
